import {
  alpha,
  Box,
  keyframes,
  Stack,
  styled,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import { getSeverityColors, PolicySeverity } from './policy-severity';

export const CardRoot = styled(Stack, {
  name: 'PolicyCardRoot',
  shouldForwardProp: (prop) => prop !== 'severity' && prop !== 'isExpanded',
})<{ severity: PolicySeverity; isExpanded: boolean }>(({ theme, severity, isExpanded }) => ({
  position: 'relative',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: '0.4375rem',
  padding: '0.3125rem 0.375rem 0.3125rem 0.5625rem',
  borderRadius: '0.375rem',
  cursor: 'pointer',
  backgroundColor: isExpanded
    ? theme.palette.custom.OV.sys.surfaceContainerHighest
    : 'transparent',

  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '0.375rem',
    bottom: '0.375rem',
    width: '0.15625rem',
    borderRadius: '0.125rem',
    backgroundColor: getSeverityColors(theme, severity).rail,
  },

  '&:hover': { backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHighest },
  // the chevron is a hover affordance; target the attribute, not an emotion class
  '&:hover [data-policy-chevron]': { opacity: 1 },
}));

/** Breathing room around the title and description, inside the card's own bounds. */
export const ROW_CONTENT_PAD_Y = '0.1875rem';

export const CardContent = styled(Box, { name: 'PolicyCardContent' })({
  flex: 1,
  minWidth: 0,
  paddingTop: ROW_CONTENT_PAD_Y,
  paddingBottom: ROW_CONTENT_PAD_Y,
});

export const RowHead = styled(Stack, { name: 'PolicyCardRowHead' })({
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: '0.5rem',
  cursor: 'pointer',
});

export const PolicyName = styled(Typography, { name: 'PolicyCardName' })(({ theme }) => ({
  fontSize: '0.78125rem',
  fontWeight: 600,
  letterSpacing: '-0.005em',
  lineHeight: 1.35,
  color: theme.palette.custom.OV.sys.onBackground,
  flex: 1,
  minWidth: 0,
  overflowWrap: 'anywhere',
}));

export const rowChevronSx =
  (expanded: boolean): SxProps<Theme> =>
  (theme) => ({
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
    marginTop: '0.0625rem',
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
    opacity: expanded ? 1 : 0,
    transition: 'transform .15s, opacity .12s',
    transform: expanded ? 'rotate(180deg)' : 'none',
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  });

export const KebabSlot = styled(Stack, { name: 'PolicyCardKebabSlot' })(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'flex-end',
  flexShrink: 0,

  '& .MuiIconButton-root': {
    width: '1.25rem',
    height: '1.25rem',
    padding: 0,
    borderRadius: '0.25rem',
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
  },
  '& .MuiSvgIcon-root': { fontSize: '1rem' },
}));

// a div, not Typography: its polymorphic `component` typing breaks under styled()
export const Description = styled('div', { name: 'PolicyCardDescription' })(({ theme }) => ({
  fontSize: '0.6875rem',
  letterSpacing: 'normal',
  color: theme.palette.custom.OV.sys.onSurface,
  lineHeight: 1.5,
}));

export const DetailBlock = styled(Box, { name: 'PolicyCardDetailBlock' })(({ theme }) => ({
  marginTop: '0.5rem',
  paddingTop: '0.5rem',
  borderTop: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
}));

export const TagRow = styled(Box, { name: 'PolicyCardTagRow' })(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.3125rem',
  fontSize: '0.6875rem',
  letterSpacing: 'normal',
  lineHeight: 'normal',
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
}));

export const getValidityChipSx =
  (isValidFuture: boolean): SxProps<Theme> =>
  (theme) => ({
    borderColor: isValidFuture
      ? theme.palette.custom.OV.sys.success
      : theme.palette.custom.OV.sys.error,
    color: isValidFuture
      ? theme.palette.custom.OV.sys.success
      : theme.palette.custom.OV.sys.error,
    height: '1.25rem',
    fontSize: '0.6875rem',
    backgroundColor: 'transparent',
    fontWeight: 500,
  });

export const Meta = styled(Typography, { name: 'PolicyCardMeta' })(({ theme }) => ({
  fontSize: '0.65625rem',
  letterSpacing: 'normal',
  lineHeight: 'normal',
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  fontStyle: 'italic',
  marginTop: '0.5rem',
}));

const cardFocus = keyframes`
  from { box-shadow: 0 0 0 0.1875rem var(--policy-focus-ring); }
  to { box-shadow: 0 0 0 0 transparent; }
`;

export const getCardFocusSx = (theme: Theme): SystemStyleObject<Theme> => ({
  '--policy-focus-ring': alpha(theme.palette.custom.OV.sys.secondary, 0.4),
  animation: `${cardFocus} 1.2s ease-out .12s both`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});
